/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Link,
  Alert,
  CircularProgress
} from '@mui/material';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const makeApiPath = (path: string) => {
  if (API_URL.endsWith('/api')) {
    return `${API_URL}${path.startsWith('/') ? path : '/' + path}`;
  }
  return `${API_URL}/api${path.startsWith('/') ? path : '/' + path}`;
};

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation
  const passwordsMatch = password === confirmPassword;
  const isPasswordValid = password.length >= 8 && 
    /[a-z]/.test(password) && 
    /[A-Z]/.test(password) && 
    /\d/.test(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError(t('invalidResetTokenError'));
      return;
    }

    if (!isPasswordValid) {
      setError(t('passwordValidationError'));
      return;
    }

    if (!passwordsMatch) {
      setError(t('passwordsDoNotMatchError'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await axios.post(makeApiPath('/auth/reset-password'), {
        token,
        password
      });
      setSuccess(true);
    } catch (err: any) {
      logger.error('Reset password error:', err);
      setError(err.response?.data?.message || t('errorOccurredTryAgain'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ minHeight: 'calc(100vh - 140px)' }}>
        <Grid item xs={12} sm={8} md={6} lg={4}>
          <Paper elevation={3} style={{ padding: '2rem', maxWidth: '500px', margin: '0 auto' }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              {t('invalidResetLink')}
            </Typography>
            
            <Alert severity="error" sx={{ mb: 3 }}>
              {t('invalidResetLinkMessage')}
            </Alert>
            
            <Box mt={3} textAlign="center">
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/forgot-password')}
              >
                {t('requestNewResetLink')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  if (success) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ minHeight: 'calc(100vh - 140px)' }}>
        <Grid item xs={12} sm={8} md={6} lg={4}>
          <Paper elevation={3} style={{ padding: '2rem', maxWidth: '500px', margin: '0 auto' }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
              {t('passwordResetSuccessful')}
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('passwordResetSuccessMessage')}
            </Alert>
            
            <Box mt={3} textAlign="center">
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/login')}
              >
                {t('goToLogin')}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container justifyContent="center" alignItems="center" style={{ minHeight: 'calc(100vh - 140px)' }}>
      <Grid item xs={12} sm={8} md={6} lg={4}>
        <Paper elevation={3} style={{ padding: '2rem', maxWidth: '500px', margin: '0 auto' }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            {t('resetYourPassword')}
          </Typography>
          
          <Typography variant="body1" align="center" paragraph>
            {t('enterNewPasswordInstruction')}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <TextField
              label={t('newPassword')}
              type="password"
              fullWidth
              margin="normal"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              error={password.length > 0 && !isPasswordValid}
              helperText={password.length > 0 && !isPasswordValid ? 
                t('passwordRequirementHelper') : ""
              }
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label={t('confirmNewPassword')}
              type="password"
              fullWidth
              margin="normal"
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              error={confirmPassword.length > 0 && !passwordsMatch}
              helperText={confirmPassword.length > 0 && !passwordsMatch ? t('passwordsDoNotMatchHelper') : ""}
              InputLabelProps={{ shrink: true }}
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              style={{ marginTop: '1.5rem' }}
              disabled={isSubmitting || !isPasswordValid || !passwordsMatch}
            >
              {isSubmitting ? <CircularProgress size={24} /> : t('resetPassword')}
            </Button>
          </form>
          
          <Box mt={3} textAlign="center">
            <Typography variant="body2">
              {t('rememberYourPassword')}{' '}
              <Link component={RouterLink} to="/login">
                {t('login')}
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ResetPasswordPage; 